import React, { useEffect, useRef, useState } from "react";
import SEO from "../components/SEO";
import { TOOLS } from "../components/booking/BookingDetails";
import { DEMO_LINK } from "../components/demoBooking";
import { withRef } from "../components/referral";
import ToolsSelect from "../components/ToolsSelect";

export default function ContactSales() {
  const [status, setStatus] = useState(""); // for feedback
  const [chosenTools, setChosenTools] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = e.target;
    const formData = new FormData(form);

    // collect multiple checkbox values into array
    const tools = formData.getAll("tools");

    const body = {
      name: formData.get("name"),
      email: formData.get("email"),
      company: formData.get("company"),
      org_size: formData.get("org_size"),
      teams: formData.get("teams"),
      tools,
      goals: withRef(formData.get("goals")),
      timeline: formData.get("timeline"),
    };

    try {
      const res = await fetch("https://api.bossdrop.ai/send-sales-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        setStatus("✅ Message sent! We'll be in touch soon.");
        form.reset();
        setChosenTools([]);
      } else {
        setStatus("⚠️ Error sending message. Try again later.");
      }
    } catch (err) {
      console.error(err);
      setStatus("⚠️ Error sending message. Try again later.");
    }
  };

  return (
    <main className="page container">
      {" "}
      <SEO
        title="BossDrop | Contact Sales"
        description="Talk to BossDrop sales about use cases, security, implementation, and ROI. Get a tailored plan and rollout timeline for your team or org."
        canonical="https://bossdrop.ai/contact-sales"
        url="https://bossdrop.ai/contact-sales"
        /* image="https://bossdrop.ai/og/pricing-1200x630.jpg" */
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "BossDrop",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: "https://bossdrop.ai/",
          publisher: {
            "@type": "Organization",
            name: "BossDrop",
            url: "https://bossdrop.ai/",
          },
        }}
        breadcrumbsJsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://bossdrop.ai/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Contact Sales",
              item: "https://bossdrop.ai/contact-sales",
            },
          ],
        }}
      />
      <section className="hero">
        <h1 className="h1 reveal" data-reveal>
          Contact Sales
        </h1>
        <p className="sub reveal" data-reveal style={{ "--d": "80ms" }}>
          Tell us about your organization and goals. We’ll shape a pilot and
          timeline.
        </p>
      </section>
      <section className="panel reveal" data-reveal>
        <form className="formGrid" onSubmit={handleSubmit}>
          <div className="field">
            <label>Full name</label>
            <input name="name" required placeholder="Jane Doe" />
          </div>
          <div className="field">
            <label>Work email</label>
            <input
              type="email"
              name="email"
              required
              placeholder="jane@company.com"
            />
          </div>
          <div className="field">
            <label>Company</label>
            <input name="company" required placeholder="Acme Inc." />
          </div>
          <div className="field">
            <label htmlFor="org-size">Org size</label>
            <FancySelect
              id="org-size"
              name="org_size"
              placeholder="Choose…"
              options={["26–100", "101–500", "501–2000", "2000+"]}
              required
            />
          </div>

          <div className="field">
            <label>How many teams?</label>
            <input name="teams" placeholder="e.g., 3 product teams" />
          </div>
          <div className="field">
            <label htmlFor="primary-tools">Primary tools</label>
            <ToolsSelect id="primary-tools" name="tools" tools={TOOLS} value={chosenTools} onChange={setChosenTools} />
          </div>

          <div className="field wide">
            <label>Goals / needs</label>
            <textarea
              name="goals"
              rows="5"
              placeholder="Where are meetings/status reporting slowing you down?"
            />
          </div>
          <div className="field wide">
            <label>Timeline & budget (optional)</label>
            <textarea
              name="timeline"
              rows="3"
              placeholder="e.g., Pilot next quarter, budget approved"
            />
          </div>

          <div className="ctaBand">
            <button className="btn btnPrimary spacing" type="submit">
              Send to sales
            </button>
            <a className="btn btnGhost" {...DEMO_LINK}>
              Request demo
            </a>
          </div>
        </form>
        {status && <p className="formStatus">{status}</p>}{" "}
        <div className="field wide">
          <p className="muted" style={{ fontSize: "12px", lineHeight: 1.4 }}>
            By entering your email, you agree to receive product updates and
            marketing emails from BossDrop. You can unsubscribe at any time. We
            respect your privacy.
          </p>
        </div>
      </section>
    </main>
  );
}

function FancySelect({ id, name, options, placeholder = "Choose…", required }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);

  const btnRef = useRef(null);
  const wrapRef = useRef(null);

  // Avoid any focus moves on the very first render
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }

    if (open) {
      // ensure we have a sane active index when opening
      const idx = Math.max(0, options.indexOf(value));
      setActiveIndex(idx);
      // keep focus on the trigger while open
      btnRef.current?.focus();
    } else {
      // closed → keep/restore focus on the trigger
    }
  }, [open, options, value]);

  // Close on outside click / Esc
  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current || wrapRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const selectAt = (i) => {
    setValue(options[i]);
    setOpen(false);
  };

  // All keyboard behavior lives on the trigger, since it keeps focus.
  const onTriggerKeyDown = (e) => {
    if (
      !open &&
      (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")
    ) {
      e.preventDefault();
      // set initial active and open
      const idx = Math.max(0, options.indexOf(value));
      setActiveIndex(idx);
      setOpen(true);
      return;
    }

    if (open) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(options.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
      } else if (e.key === "Home") {
        e.preventDefault();
        setActiveIndex(0);
      } else if (e.key === "End") {
        e.preventDefault();
        setActiveIndex(options.length - 1);
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (activeIndex >= 0) selectAt(activeIndex);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
    }
  };

  return (
    <div className={`select ${open ? "open" : ""}`} ref={wrapRef}>
      {/* Hidden input so Formspree receives the value */}
      <input type="hidden" name={name} value={value} required={required} />

      <button
        id={id}
        ref={btnRef}
        type="button"
        className="selectTrigger"
        aria-haspopup="listbox"
        aria-expanded={open ? "true" : "false"}
        aria-controls={`${id}-listbox`}
        data-placeholder={value ? "false" : "true"}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="selectValue">{value || placeholder}</span>
        <span className="selectCaret" aria-hidden="true" />
      </button>

      {open && (
        <ul
          id={`${id}-listbox`}
          className="selectPanel scrollable"
          role="listbox"
          // list never receives focus; it’s purely click/hover
        >
          {options.map((opt, i) => {
            const selected = opt === value;
            const active = i === activeIndex;
            return (
              <li
                id={`${id}-opt-${i}`}
                key={opt}
                role="option"
                aria-selected={selected ? "true" : "false"}
                className={`selectOption${selected ? " selected" : ""}${
                  active ? " active" : ""
                }`}
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()} // keep focus on button
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => selectAt(i)}
              >
                <span className="optionLabel">{opt}</span>
                {selected && (
                  <span className="optionCheck" aria-hidden="true" />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
